import { useEffect, useRef, useState } from "react";

type CarouselItem = {
  id: number;
  selected: boolean;
  title: string;
};

const initialData: CarouselItem[] = [
  { id: 1, selected: true, title: "Image coursel minimap" },
  { id: 2, selected: false, title: "Image coursel minimap" },
  { id: 3, selected: false, title: "Image coursel minimap" },
  { id: 4, selected: false, title: "Image coursel minimap" },
  { id: 5, selected: false, title: "Image coursel minimap" },
  { id: 6, selected: false, title: "Image coursel minimap" },
];

export function ImageCarouselMinimapPage() {
  const [items, setItems] = useState<CarouselItem[]>(initialData);
  const [scrollY, setScrollY] = useState(0);
  const [indicatorPosition, setIndicatorPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const minimapBorderColor = "rgb(var(--carousel-minimap-border))";

  const containerRef = useRef<HTMLDivElement | null>(null);
  const minimapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.title = "Image Coursel with Minimap | Yash Bindal";

    const descriptionTag =
      document.querySelector('meta[name="description"]') ?? document.createElement("meta");

    descriptionTag.setAttribute("name", "description");
    descriptionTag.setAttribute("content", "Image Coursel with Minimap x React");

    if (!descriptionTag.parentElement) {
      document.head.appendChild(descriptionTag);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current || !minimapRef.current) {
      return;
    }

    const containerTop = containerRef.current.offsetTop;
    const containerHeight = containerRef.current.scrollHeight;
    const viewportHeight = window.innerHeight;
    const scrolledIntoContainer = Math.max(0, scrollY - containerTop);
    const scrollableArea = Math.max(1, containerHeight - viewportHeight);
    const progress = Math.max(0, Math.min(1, scrolledIntoContainer / scrollableArea));

    const minimapHeight = minimapRef.current.scrollHeight;
    const indicatorHeight = 112;
    const maxPosition = Math.max(1, minimapHeight - indicatorHeight);
    setIndicatorPosition(progress * maxPosition);
  }, [scrollY]);

  const updateScrollFromMouse = (clientY: number, forceInstant = false) => {
    if (!minimapRef.current || !containerRef.current) {
      return;
    }

    const rect = minimapRef.current.getBoundingClientRect();
    const mouseY = clientY - rect.top;

    const minimapHeight = minimapRef.current.scrollHeight;
    const indicatorHeight = 112;
    const maxPosition = Math.max(1, minimapHeight - indicatorHeight);
    const centeredMouseY = mouseY - indicatorHeight / 2;
    const progress = Math.max(0, Math.min(1, centeredMouseY / maxPosition));

    const containerTop = containerRef.current.offsetTop;
    const containerHeight = containerRef.current.scrollHeight;
    const viewportHeight = window.innerHeight;
    const scrollableArea = Math.max(1, containerHeight - viewportHeight);

    const targetScroll = containerTop + progress * scrollableArea;

    window.scrollTo({
      top: targetScroll,
      behavior: forceInstant || isDragging ? "auto" : "smooth",
    });
  };

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      updateScrollFromMouse(event.clientY, true);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const toggleSelection = (id: number) => {
    setItems(currentItems =>
      currentItems.map(item =>
        item.id === id ? { ...item, selected: !item.selected } : item
      )
    );
  };

  return (
    <div className="container mx-auto grid min-h-screen max-w-5xl grid-cols-1 gap-8 px-4 py-20 md:grid-cols-[1fr_auto]">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col items-start gap-1">
          <h1 className="text-xl font-medium" style={{ color: "rgb(var(--text-primary))" }}>
            Image Coursel with Minimap
          </h1>
          <h4 className="font-light" style={{ color: "rgb(var(--text-secondary))" }}>
            Image Coursel x React
          </h4>
        </div>
        <div className="flex w-full flex-col items-center gap-4" ref={containerRef}>
          {items.map(item => (
            <button
              key={item.id}
              className="relative h-[24rem] w-full max-w-80 rounded-xl border transition-colors duration-200 sm:h-[30rem]"
              style={{
                backgroundColor: item.selected ? "rgb(var(--bg-tertiary))" : "rgb(var(--bg-secondary))",
                borderColor: item.selected ? "rgb(var(--border-color))" : "rgb(var(--border-color) / 0.75)",
              }}
              onClick={() => toggleSelection(item.id)}
              type="button"
              aria-label={item.title}
            >
              <div className="absolute top-2 right-2">
                {item.selected ? (
                  <svg
                    width="24px"
                    height="24px"
                    viewBox="0 0 0.72 0.72"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ fill: "rgb(var(--text-primary))" }}
                  >
                    <path d="M0.3 0.54a0.03 0.03 0 0 1-0.021-0.009l-0.15-0.15a0.03 0.03 0 0 1 0.043-0.043l0.129 0.129 0.249-0.249a0.03 0.03 0 1 1 0.043 0.043l-0.27 0.27A0.03 0.03 0 0 1 0.3 0.54" />
                  </svg>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div
        className="sticky top-10 hidden h-fit cursor-pointer flex-col gap-4 rounded-xl p-2 select-none md:flex"
        ref={minimapRef}
        role="button"
        tabIndex={0}
        onMouseDown={event => {
          setIsDragging(true);
          updateScrollFromMouse(event.clientY, true);
        }}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            const rect = minimapRef.current?.getBoundingClientRect();
            if (!rect) {
              return;
            }

            updateScrollFromMouse(rect.top + rect.height / 2, false);
          }
        }}
      >
        {items.map(item => (
          <div
            key={item.id}
            className="h-10 w-10 rounded-md border transition-colors duration-200"
            style={{
              backgroundColor: item.selected ? "rgb(var(--bg-tertiary))" : "rgb(var(--bg-secondary))",
              borderColor: item.selected ? "rgb(var(--border-color))" : "rgb(var(--border-color) / 0.75)",
            }}
          />
        ))}
        <div
          className="absolute left-0 h-28 w-full rounded-xl border-2 transition-all duration-150"
          style={{ top: `${indicatorPosition}px`, borderColor: minimapBorderColor }}
        />
      </div>
    </div>
  );
}
